import { useMemo } from "react";
import { useBiens } from "@/hooks/useBiens";
import { useRole, type AppRole } from "@/contexts/RoleContext";
import { useAuth } from "@/contexts/AuthContext";
import { deriveTasks, groupTasks } from "@/v2/lib/tasks";

/**
 * Hook principal de l'Inbox V2. Récupère les biens via React Query (cache
 * partagé avec la V1) et dérive les tâches selon le rôle courant.
 *
 * Le ctx (collabId / prestataireId) est dérivé du profile connecté.
 *
 * roleOverride : utile pour la simulation admin (admin qui visualise l'inbox
 * d'un autre rôle) — sinon on prend le rôle courant.
 */
export function useTasks(roleOverride?: AppRole) {
  const { role: ctxRole } = useRole();
  const { profile } = useAuth();
  const role = roleOverride ?? ctxRole;

  const { data: biens, isLoading, error } = useBiens(
    // On utilise le rôle courant pour la requête (RLS Supabase). Pour la
    // simulation admin, on lit avec un role qui a les droits adéquats.
    role === "admin" ? "admin" : role,
  );

  const tasks = useMemo(() => {
    if (!biens) return [];
    return deriveTasks(role, biens, {
      collabId: profile?.id,
      prestataireId: profile?.id, // les prestataires sont aussi des profiles
    });
  }, [biens, role, profile?.id]);

  const groups = useMemo(() => groupTasks(tasks), [tasks]);

  return { tasks, groups, isLoading, error, totalBiens: biens?.length ?? 0 };
}
